/**
 * MultiSelectRenderer — toggleable chips + a submit button. Submits as
 * `structured_value: string[]` so the agent gets the picked values as a
 * list (vs quick_replies which is one-of).
 *
 * `min`/`max` constraints disable the submit button until satisfied — the
 * server is still authoritative but we don't want to round-trip an
 * obviously-invalid selection.
 *
 * History: chosen chips stay highlighted; submit button hides.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MultiSelectContentItem, QuickReplyOption } from '@wg/types';
import type { Renderer } from '../registry';

const MultiSelectRenderer: Renderer = ({ item, ctx }) => {
  const { t } = useTranslation();
  const it = item as MultiSelectContentItem;
  const inert = ctx.isHistory;
  const answered = useMemo(() => {
    const a = ctx.answeredWith;
    return Array.isArray(a) ? (a as unknown[]).map(String) : [];
  }, [ctx.answeredWith]);
  const [picked, setPicked] = useState<string[]>(answered);

  if (!it.options || it.options.length === 0) return null;

  const min = it.min ?? 0;
  const max = it.max ?? Infinity;
  const valid = picked.length >= min && picked.length <= max;

  const toggle = (value: string) => {
    setPicked(prev => {
      if (prev.includes(value)) return prev.filter(v => v !== value);
      if (prev.length >= max) return prev;
      return [...prev, value];
    });
  };

  const onSubmit = () => {
    if (inert || !valid || picked.length === 0) return;
    const labels = picked
      .map(v => it.options.find(o => o.value === v)?.label ?? v)
      .join(', ');
    ctx.onSendMessage(labels, {
      in_response_to: it.prompt_id,
      structured_value: picked,
    });
  };

  return (
    <div
      className="wg-chat__multi"
      data-inert={inert ? 'true' : undefined}
      role="group"
      aria-label={it.question || t('multi_select.default_question')}
    >
      {it.question && <p className="wg-chat__chips-q">{it.question}</p>}
      <div className="wg-chat__chips-row">
        {it.options.map((opt: QuickReplyOption, idx: number) => {
          const isOn = inert
            ? answered.includes(opt.value)
            : picked.includes(opt.value);
          const cls = [
            'wg-chat__chip',
            opt.weight ? `wg-chat__chip--${opt.weight}` : null,
            isOn ? 'is-chosen' : null,
            inert && !isOn ? 'is-muted' : null,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={`${opt.value}-${idx}`}
              type="button"
              className={cls}
              disabled={inert}
              aria-pressed={isOn}
              onClick={() => toggle(opt.value)}
            >
              {opt.icon && (
                <span className="wg-chat__chip-icon">{opt.icon}</span>
              )}
              <span className="wg-chat__chip-label">{opt.label}</span>
            </button>
          );
        })}
      </div>
      {!inert && (
        <div className="wg-chat__multi-actions">
          <span className="wg-chat__multi-count">
            {max !== Infinity
              ? t('multi_select.selected_count_max', {
                  count: picked.length,
                  max,
                })
              : t('multi_select.selected_count', { count: picked.length })}
          </span>
          <button
            type="button"
            className="wg-chat__chip wg-chat__chip--primary"
            disabled={!valid || picked.length === 0}
            onClick={onSubmit}
          >
            {it.submit_label || t('common.submit')}
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiSelectRenderer;
